import * as UI from "@goodgym/components";
import AddressSearchDatabase from "@goodgym/components/AddressSearchDatabase";
import { NewAddressData } from "@goodgym/components/AddressSearchDatabase/CreateAddressForm";
import Banner from "@goodgym/components/Banner";
import { useSessionPageQuery } from "@goodgym/graphql/hooks";
import { useCreateGroupFitnessActivity } from "@goodgym/graphql/mutations/CreateGroupFitnessActivity";
import { useUpdateGroupFitnessActivity } from "@goodgym/graphql/mutations/UpdateGroupFitnessActivity";
import * as T from "@goodgym/graphql/types";
import React, { useState } from "react";
import { useParams } from "react-router-dom";
import { convertTo12HourFormat, timeIntervals } from "./components/helpers";
import * as Images from "./images";
export type GroupFitnessActivityPageProps = UI.BoxProps & {
  panel?: boolean;
  slug?: string;
  groupFitnessActivity?: T.GroupFitnessActivityFragment;
};

const GroupFitnessActivityPage: React.FC<GroupFitnessActivityPageProps> = (
  props
) => {
  const { slug, id } = useParams();
  const { data } = useSessionPageQuery({
    variables: { slug: props.slug || slug! },
    skip: !(props.slug || slug),
  });

  const sessionId = data?.session.id;
  const sessionDate = data?.session.startedAt;

  const activity =
    id && data?.session?.groupFitnessActivities
      ? data.session.groupFitnessActivities.find((a) => a.id === id)
      : null;
  const isEditing = !!activity;
  const [description, setDescription] = useState("");
  const [distance, setDistance] = useState("2");
  const [hasToilets, setHasToilets] = useState(false);
  const [hasBagDrop, setHasBagDrop] = useState(false);
  const [hasAccessibleToilets, setHasAccessibleToilets] = useState(false);
  const [returnJourney, setReturnJourney] = useState(false);
  const [type, setType] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [address, setAddress] = useState<
    NewAddressData | T.AddressSearchDatabaseResultFragment | null
  >(null);
  const [newAddress, setNewAddress] = useState<NewAddressData | null>(null);

  React.useEffect(() => {
    if (!activity) return;

    setDescription(activity.description || "");
    setDistance(activity.distance?.toString() || "2");
    setHasToilets(activity.toilets || false);
    setHasBagDrop(activity.bagDrop || false);
    setHasAccessibleToilets(activity.accessibleToilets || false);
    setReturnJourney(activity.runReturn || false);
    setType(
      activity.type === "Walk"
        ? "GroupFitnessWalk"
        : activity.type === "Run"
        ? "GroupFitnessRun"
        : activity.type === "Cycle"
        ? "GroupFitnessCycle"
        : ""
    );

    if (activity.startedAt) {
      setStartsAt(
        new Date(activity.startedAt)
          .toLocaleTimeString("en-GB", {
            hour12: false,
            hour: "2-digit",
            minute: "2-digit",
          })
          .replace(":", "")
      );
    }

    // Set address from activity
    if (activity.address) {
      setAddress({
        __typename: "AddressSearchDatabaseResult",
        id: activity.address.id,
        addressLine1: activity.address.line1,
        addressLine2: activity.address.line2 || "",
        city: activity.address.city || "",
        postCode: activity.address.postcode,
        compositeAddress: `${activity.address.line1}, ${activity.address.line2}, ${activity.address.city}, ${activity.address.postcode}`,
      });
    }
  }, [activity]);

  const handleNewAddressCreated = (addressData: NewAddressData) => {
    setNewAddress(addressData);
    const formattedAddress = {
      addressLine1: addressData.addressLine1,
      addressLine2: addressData.addressLine2,
      city: addressData.city,
      postCode: addressData.postCode,
      region: addressData.region,
    };
    //@ts-ignore
    setAddress(formattedAddress);
  };

  const handletypeChange = (event: any) => {
    setType(event.target.value);
  };

  const handleCreate = useCreateGroupFitnessActivity();
  const handleUpdate = useUpdateGroupFitnessActivity();
  const formatTimeString = (time: string) => {
    if (time.length === 4) {
      return `${time.slice(0, 2)}:${time.slice(2)}`;
    }
    return time;
  };

  const sessionDateOnly = sessionDate
    ? new Date(sessionDate).toISOString().split("T")[0]
    : null;


  function isDatabaseAddress(
    address:
      | NewAddressData
      | T.AddressSearchDatabaseResultFragment
      | null
      | undefined
  ): address is T.AddressSearchDatabaseResultFragment {
    return !!address && "id" in address && typeof address.id === "string";
  }

  const onClickSubmit = isEditing
    ? handleUpdate({
        id: activity!.id,
        sessionSlug: data?.session.slug || "",
        distance: parseFloat(distance),
        toilets: hasToilets,
        bagDrop: hasBagDrop,
        accessibleToilets: hasAccessibleToilets,
        runReturn: returnJourney || false,
        type,
        startedAt: startsAt
          ? new Date(
              `${sessionDateOnly}T${formatTimeString(startsAt)}`
            ).toISOString()
          : undefined,
        description,
        addressId: isDatabaseAddress(address) ? address.id : undefined,
        addressLine1: newAddress?.addressLine1,
        addressLine2: newAddress?.addressLine2,
        city: newAddress?.city,
        postCode: newAddress?.postCode,
        region: newAddress?.region,
      })
    : handleCreate({
        sessionId: sessionId || "",
        distance: parseFloat(distance),
        toilets: hasToilets,
        bagDrop: hasBagDrop,
        accessibleToilets: hasAccessibleToilets,
        runReturn: returnJourney || false,
        type,
        startedAt:
          startsAt &&
          new Date(
            `${sessionDateOnly}T${formatTimeString(startsAt)}`
          ).toISOString(),
        description,
        addressId: isDatabaseAddress(address) ? address.id : undefined,
        addressLine1: newAddress?.addressLine1,
        addressLine2: newAddress?.addressLine2,
        city: newAddress?.city,
        postCode: newAddress?.postCode,
        region: newAddress?.region,
        sessionSlug: data?.session.slug || "",
      });
  return (
    <>
      <Banner
        title="Group Fitness Activity"
        image={Images.GroupFitnessBanner}
        tagline={data?.session.title}
      />

      <UI.Container sx={{ px: 2, py: 4 }}>
        <UI.Typography variant="h5" sx={{ mb: 4 }}>
          Add an option for GoodGymers to get to/from the task. This can be a
          run, cycle or a walk. Make sure to select somewhere accessible and
          public to meet.
        </UI.Typography>

        <UI.Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <UI.Box>
            <UI.Typography variant="h6" sx={{ fontWeight: "medium" }}>
              Starting Point
            </UI.Typography>
            <UI.Typography variant="body2" sx={{ mb: 1 }}>
              Search for an address by partial address or postcode (minimum 3
              characters)
            </UI.Typography>
            <AddressSearchDatabase
              value={address}
              onNewAddressCreated={handleNewAddressCreated}
              onAddressSelected={(address) => {
                setAddress(address);
              }}
            />
          </UI.Box>

          <UI.Box>
            <UI.Typography variant="body1" sx={{ mb: 1 }}>
              Location has
            </UI.Typography>
            <UI.FormGroup row sx={{ gap: 3 }}>
              <UI.FormControlLabel
                control={
                  <UI.Checkbox
                    checked={hasToilets}
                    onChange={(e) => setHasToilets(e.target.checked)}
                  />
                }
                label="Toilets"
              />
              <UI.FormControlLabel
                control={
                  <UI.Checkbox
                    checked={hasBagDrop}
                    onChange={(e) => setHasBagDrop(e.target.checked)}
                  />
                }
                label="Bag drop"
              />
              <UI.FormControlLabel
                control={
                  <UI.Checkbox
                    checked={hasAccessibleToilets}
                    onChange={(e) => setHasAccessibleToilets(e.target.checked)}
                  />
                }
                label="Ramps and accessible toilets"
              />
            </UI.FormGroup>
          </UI.Box>

          <UI.Box>
            <UI.FormControl variant="outlined" sx={{ maxWidth: "240px" }}>
              <UI.InputLabel htmlFor="distance">Distance (km)*</UI.InputLabel>
              <UI.Input
                id="distance"
                type="number"
                value={distance}
                onChange={(e) => setDistance(e.target.value)}
                inputProps={{ min: "0" }}
              />
            </UI.FormControl>
          </UI.Box>

          <UI.FormControlLabel
            control={
              <UI.Checkbox
                checked={returnJourney}
                onChange={(e) => setReturnJourney(e.target.checked)}
              />
            }
            label="Return journey: we'll be walking, running, cycling back to this location after the task"
          />

          <UI.Box>
            <UI.FormControl variant="outlined" sx={{ width: "400px" }}>
              <UI.InputLabel id="travel-mode-label">
                Are you walking, cycling or running?*
              </UI.InputLabel>
              <UI.Select
                labelId="travel-mode-label"
                id="travel-mode"
                value={type}
                fullWidth
                onChange={handletypeChange}
                label="Are you walking, cycling or running?*"
              >
                <UI.MenuItem value="GroupFitnessWalk">Walking</UI.MenuItem>
                <UI.MenuItem value="GroupFitnessRun">Running</UI.MenuItem>
                <UI.MenuItem value="GroupFitnessCycle">Cycling</UI.MenuItem>
              </UI.Select>
            </UI.FormControl>
          </UI.Box>

          <UI.Box>
            <UI.FormControl fullWidth>
              <UI.InputLabel id="time-select-label">
                When are you setting off?
              </UI.InputLabel>

              <UI.Select
                id="time-select"
                label="When are you setting off?"
                onChange={(e) => setStartsAt(e.target.value)}
                value={startsAt}
              >
                {timeIntervals.map((time) => (
                  <UI.MenuItem key={time} value={time} aria-label={time}>
                    {convertTo12HourFormat(time)}
                  </UI.MenuItem>
                ))}
              </UI.Select>
            </UI.FormControl>
          </UI.Box>

          <UI.Box>
            <UI.FormControl fullWidth>
              <UI.TextField
                id="description"
                multiline
                rows={4}
                label="Describe your pace, your route and any other information that
                would help people decide if they want to join you."
                placeholder="Max 200 characters."
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 200 }}
              />
            </UI.FormControl>
          </UI.Box>

          <UI.Box>
            <UI.Button
              variant="contained"
              sx={{ "&:hover": { bgcolor: "#b91c1c" } }}
              onClick={onClickSubmit}
            >
              {isEditing
                ? "UPDATE GROUP FITNESS ACTIVITY"
                : "CREATE GROUP FITNESS ACTIVITY"}
            </UI.Button>
          </UI.Box>
        </UI.Box>
      </UI.Container>
    </>
  );
};

export default GroupFitnessActivityPage;
